import cx from "classnames";
import { Header } from "@tanstack/react-table";

import styles from "./ResizeHandle.module.scss";

/**
 * Props for the ResizeHandle component
 * @property {Header<T, unknown>} header - The header instance from TanStack Table
 * @property {() => void} [onResizeStart] - Callback fired before resize begins, useful for initializing state
 */
export type ResizeHandleProps<T> = {
  header: Header<T, unknown>;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
};

/**
 * Resize handle component resizing table columns
 * @param props - The props for the ResizeHandle component
 * @param props.header - The header of the ResizeHandle component
 * @param props.onResizeStart - Optional callback fired before resize begins
 */
export function ResizeHandle<T>({
  header,
  onResizeStart,
  onResizeEnd,
}: ResizeHandleProps<T>) {
  const classes = cx(styles["resize-handle"], {
    [styles["resizing"]]: header.column.getIsResizing(),
  });

  const handleResizeStart = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    onResizeStart?.();
    header.getResizeHandler()(e);
  };

  return (
    <div
      role="button"
      // TODO: enable keyboard resizing
      tabIndex={-1}
      onDoubleClick={() => header.column.resetSize()}
      onMouseDown={handleResizeStart}
      onTouchStart={handleResizeStart}
      onMouseUp={onResizeEnd}
      onTouchEnd={onResizeEnd}
      className={classes}
      aria-label="Resize column"
      aria-pressed={header.column.getIsResizing()}
      aria-roledescription="Resize handle"
    />
  );
}
